import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList, ClothingItem } from '../types';
import {
  AppHeader,
  Button,
  ScreenLayout,
  ImageGallery,
  ReportModal,
  ClubMark,
  ConditionBadge,
  PageTitle,
} from '../components';
import { useResponsive } from '../utils/responsive';
import { formatRelativeTime } from '../utils/relativeTime';
import { Colors, Fonts, Radii, Spacing, Typography, StatusTints } from '../constants';
import { itemsService } from '../services/items';
import { analyticsService } from '../services/analytics';
import { moderationService, ReportReason } from '../services/moderation';
import { conversationsService } from '../services/conversations';
import { useDialog, useUser } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'ItemDetails'>;

export const ItemDetailsScreen: React.FC<Props> = ({ route, navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const { user } = useUser();
  const { itemId } = route.params;
  const [item, setItem] = useState<ClothingItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [existingConversationId, setExistingConversationId] = useState<string | null>(null);
  const [ownerViewCount, setOwnerViewCount] = useState<number | null>(null);
  const { isDesktop } = useResponsive();
  const insets = useSafeAreaInsets();

  const currentUserId = user?.id ?? null;

  // Helper functions
  const loadItem = useCallback(async () => {
    try {
      const data = await itemsService.getItemById(itemId);
      setItem(data);
    } catch (error: any) {
      dialog.alert(t('common.errors.title'), t('screens.itemDetails.loadFailed'), 'error');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  }, [itemId, dialog, t, navigation]);

  // PostHog funnel event with the item's category (anonymous; only once the
  // item is loaded and never for the seller's own listing). The DB-side view
  // count is recorded separately below via recordItemView.
  useEffect(() => {
    if (!item || !currentUserId || item.seller_id === currentUserId) return;
    analyticsService.trackItemViewed(item.category);
  }, [item?.id, currentUserId]);

  // Owner sees how often their own listing was viewed
  useEffect(() => {
    if (!item || !currentUserId || item.seller_id !== currentUserId) {
      setOwnerViewCount(null);
      return;
    }
    itemsService.getItemViewCount(item.id).then(setOwnerViewCount).catch(() => setOwnerViewCount(null));
  }, [item?.id, currentUserId]);

  const checkExistingConversation = React.useCallback(async () => {
    if (!item || !currentUserId) return;

    try {
      const conversation = await conversationsService.getConversationForItem(item.id);
      setExistingConversationId(conversation?.id || null);
    } catch (error) {
      // Silently fail - not critical
      setExistingConversationId(null);
    }
  }, [item, currentUserId]);

  // Effects
  useEffect(() => {
    loadItem();
  }, [itemId]);

  // Count the view once per visit; dedup per user/day happens server-side
  useEffect(() => {
    itemsService.recordItemView(itemId).catch(() => {
      // Not critical — never block the screen on view tracking
    });
    analyticsService.trackItemView(itemId);
  }, [itemId]);

  // Check for existing conversation when item loads
  useEffect(() => {
    if (item && currentUserId && currentUserId !== item.seller_id) {
      checkExistingConversation();
    }
  }, [item, currentUserId, checkExistingConversation]);

  // Refresh item data when screen comes back into focus (e.g., after editing)
  useFocusEffect(
    React.useCallback(() => {
      loadItem();
    }, [loadItem])
  );

  // Re-check conversation when screen comes back into focus
  // (e.g., after deleting an empty conversation in ChatScreen)
  useFocusEffect(
    React.useCallback(() => {
      if (item && currentUserId && currentUserId !== item.seller_id) {
        checkExistingConversation();
      }
    }, [item, currentUserId, checkExistingConversation])
  );

  const handleStartConversation = async () => {
    if (!currentUserId) {
      navigation.navigate('Login');
      return;
    }
    if (!item?.seller_id) return;

    // If conversation already exists, navigate to it directly
    if (existingConversationId) {
      navigation.navigate('Chat', { conversationId: existingConversationId });
      return;
    }

    try {
      // Get or create conversation for this item
      const conversation = await conversationsService.getOrCreateConversation(item.id, item.seller_id);

      // Update state to reflect conversation exists
      setExistingConversationId(conversation.id);

      // Navigate to chat screen
      navigation.navigate('Chat', { conversationId: conversation.id });
    } catch (error: any) {
      if (error.message.includes('already have a conversation')) {
        // If conversation already exists, the service returned it - this shouldn't happen with getOrCreateConversation
        dialog.alert(t('common.errors.title'), t('screens.itemDetails.conversationExists'), 'info');
      } else {
        dialog.alert(t('common.errors.title'), t('screens.itemDetails.startConversationFailed'), 'error');
      }
    }
  };

  const handleDelete = async () => {
    if (!item) return;

    const confirmed = await dialog.confirm(
      t('screens.itemDetails.deleteConfirm'),
      t('screens.itemDetails.deleteConfirmMessage'),
      { variant: 'danger' }
    );

    if (!confirmed) return;

    try {
      await itemsService.deleteItem(item.id);
      navigation.goBack();
    } catch (error: any) {
      if (error.message?.includes('transaction in progress')) {
        dialog.alert(
          t('common.errors.title'),
          t('screens.itemDetails.cannotDeleteTransaction'),
          'warning'
        );
      } else {
        dialog.alert(t('common.errors.title'), t('screens.itemDetails.deleteFailed'), 'error');
      }
    }
  };

  const handleEdit = () => {
    if (!item) return;
    navigation.navigate('ItemForm', { itemId: item.id });
  };

  const handleReport = () => {
    if (!currentUserId) {
      navigation.navigate('Login');
      return;
    }
    setShowReportModal(true);
  };

  const handleSubmitReport = async (reason: ReportReason, details: string) => {
    if (!item) return;

    try {
      await moderationService.reportItem(item.id, reason, details);
      setShowReportModal(false);
      dialog.alert(
        t('common.success'),
        t('screens.itemDetails.reportSuccess'),
        'success'
      );
    } catch (error: any) {
      setShowReportModal(false);
      if (error.message.includes('already reported')) {
        dialog.alert(
          t('common.errors.title'),
          t('screens.itemDetails.alreadyReported'),
          'warning'
        );
      } else {
        dialog.alert(
          t('common.errors.title'),
          error.message || t('screens.itemDetails.reportFailed'),
          'error'
        );
      }
    }
  };

  const renderMobileHeader = (title: string, statusBadge?: { label: string; variant: 'warning' | 'neutral' }) =>
    !isDesktop ? (
      <AppHeader
        navigation={navigation}
        currentScreen="ItemDetails"
        title={title}
        showBackButton={true}
        statusBadge={statusBadge}
      />
    ) : null;

  if (loading) {
    return (
      <ScreenLayout fullWidth={true}>
        <View style={styles.container}>
          {renderMobileHeader(t('screens.itemDetails.title'))}
          <View style={styles.centerContainer}>
            <Text>{t('common.loading')}</Text>
          </View>
        </View>
      </ScreenLayout>
    );
  }

  if (!item) {
    return (
      <ScreenLayout fullWidth={true}>
        <View style={styles.container}>
          {renderMobileHeader(t('screens.itemDetails.title'))}
          <View style={styles.centerContainer}>
            <Text>{t('screens.itemDetails.loadFailed')}</Text>
          </View>
        </View>
      </ScreenLayout>
    );
  }

  const isOwnItem = currentUserId === item.seller_id;
  const isAvailable = item.status === 'available';
  const sellerName = [item.seller?.first_name, item.seller?.last_name].filter(Boolean).join(' ') || item.seller?.name || '';
  const statusBadge = !isAvailable
    ? { label: t(`enums.status.${item.status}`), variant: item.status === 'reserved' ? 'warning' as const : 'neutral' as const }
    : undefined;

  // Status chip tint: available → success, reserved → accent, sold → neutral
  const statusTint = isAvailable
    ? StatusTints.success
    : item.status === 'reserved'
      ? StatusTints.primary
      : StatusTints.neutral;

  const listed = t('screens.itemDetails.listed', { when: formatRelativeTime(item.created_at, t) });
  const subtitle = item.club ? `${item.club.name} · ${listed}` : listed;
  const hasPhotos = !!item.image_urls && item.image_urls.length > 0;

  // Actions: exactly one accent fill per view.
  const actions = isOwnItem ? (
    <>
      {isAvailable && (
        <Button
          title={t('screens.itemDetails.markSoldCta')}
          onPress={() => navigation.navigate('MarkAsSold', { itemId: item.id })}
          style={styles.actionGrow}
        />
      )}
      {isAvailable && (
        <Button
          title={t('screens.itemDetails.editItem')}
          variant="secondary"
          onPress={handleEdit}
        />
      )}
      <Button
        title={t('screens.itemDetails.deleteConfirm')}
        variant="ghost"
        onPress={handleDelete}
        style={!isAvailable ? styles.actionGrow : undefined}
      />
    </>
  ) : (
    <>
      {isAvailable && (
        <Button
          title={existingConversationId ? t('screens.itemDetails.inConversation') : t('screens.itemDetails.startConversation')}
          onPress={handleStartConversation}
          style={styles.actionGrow}
          testID="contact-seller"
        />
      )}
      <Button
        title={t('screens.itemDetails.reportItem')}
        variant="ghost"
        onPress={handleReport}
        style={!isAvailable ? styles.actionGrow : undefined}
      />
    </>
  );

  // Price row, facts, description and seller — identical on both breakpoints
  const details = (
    <>
      <View style={styles.priceRow}>
        <Text style={styles.price}>€{Number(item.price || 0).toFixed(2)}</Text>
        <View style={styles.chips}>
          <View style={[styles.statusChip, { backgroundColor: statusTint.bg }]}>
            <Text style={[styles.statusChipText, { color: statusTint.fg }]}>
              {t(`enums.status.${item.status}`)}
            </Text>
          </View>
          <ConditionBadge condition={item.condition} />
        </View>
      </View>

      <View style={styles.facts}>
        <View style={styles.fact}>
          <Text style={[Typography.overline, styles.factLabel]}>{t('screens.itemDetails.size')}</Text>
          <Text style={styles.factValue}>{item.size}</Text>
        </View>
        <View style={styles.fact}>
          <Text style={[Typography.overline, styles.factLabel]}>{t('screens.itemDetails.category')}</Text>
          <Text style={styles.factValue}>
            {t(`enums.category.${item.category}`, { defaultValue: item.category })}
          </Text>
        </View>
        <View style={styles.fact}>
          {item.club ? (
            <>
              <Text style={[Typography.overline, styles.factLabel]}>{t('screens.itemDetails.club')}</Text>
              <TouchableOpacity onPress={() => navigation.navigate('ClubDetail', { clubId: item.club!.id })}>
                <Text style={styles.factValue}>{item.club.name}</Text>
              </TouchableOpacity>
            </>
          ) : null}
        </View>
      </View>

      <View style={styles.descriptionBlock}>
        <Text style={styles.blockLabel}>{t('screens.itemDetails.description')}</Text>
        <Text style={styles.description}>
          {item.description || t('screens.itemDetails.noDescription')}
        </Text>
      </View>

      {sellerName ? (
        <View style={styles.sellerRow}>
          <ClubMark name={sellerName} size={40} />
          <View style={styles.sellerText}>
            <Text style={[Typography.cardTitle, styles.sellerName]} numberOfLines={1}>{sellerName}</Text>
            {item.club ? (
              <Text style={styles.sellerMeta} numberOfLines={1}>
                {t('screens.itemDetails.memberOf', { club: item.club.name })}
              </Text>
            ) : null}
          </View>
        </View>
      ) : null}

      {isOwnItem && ownerViewCount != null && (
        <View style={styles.viewCountRow}>
          <Ionicons name="eye-outline" size={16} color={Colors.textMedium} />
          <Text style={styles.viewCountText}>
            {t('screens.itemDetails.viewCount', { count: ownerViewCount })}
          </Text>
        </View>
      )}
    </>
  );

  return (
    <ScreenLayout maxWidth={800} desktopPadding>
      <View style={styles.container}>
        {renderMobileHeader(item.title, statusBadge)}
        <ScrollView style={styles.scrollView}>
          {isDesktop ? (
            <>
              <PageTitle
                title={item.title}
                subtitle={subtitle}
                onBack={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Browse', {}))}
              />
              {hasPhotos ? (
                <ImageGallery publicIds={item.image_urls!} thumbnails />
              ) : (
                <View style={styles.photoPlaceholderDesktop}>
                  <Ionicons name="image-outline" size={40} color={Colors.border} />
                </View>
              )}
              {details}
              <View style={styles.actions}>{actions}</View>
            </>
          ) : (
            <>
              {hasPhotos ? (
                <ImageGallery publicIds={item.image_urls!} fullBleed />
              ) : (
                <View style={styles.photoPlaceholder}>
                  <Ionicons name="image-outline" size={40} color={Colors.border} />
                </View>
              )}
              <View style={styles.contentMobile}>{details}</View>
            </>
          )}

          <ReportModal
            visible={showReportModal}
            onClose={() => setShowReportModal(false)}
            onSubmit={handleSubmitReport}
            itemTitle={item.title}
          />
        </ScrollView>

        {/* Mobile: sticky bottom action bar */}
        {!isDesktop && (
          <View style={[styles.actionBar, { paddingBottom: Math.max(insets.bottom, Spacing.md) + Spacing.sm }]}>
            {actions}
          </View>
        )}
      </View>
    </ScreenLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  scrollView: {
    flex: 1,
  },
  contentMobile: {
    paddingHorizontal: Spacing.base,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoPlaceholder: {
    width: '100%',
    aspectRatio: 1,
    backgroundColor: Colors.card,
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoPlaceholderDesktop: {
    width: '100%',
    aspectRatio: 4 / 3,
    borderRadius: Radii.card,
    backgroundColor: Colors.card,
    justifyContent: 'center',
    alignItems: 'center',
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.base,
    marginTop: Spacing.xl,
    paddingBottom: Spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  price: {
    ...Typography.price,
    // The detail page shows the price larger than a card does.
    fontSize: 28,
    lineHeight: 34,
    color: Colors.textDark,
    flexShrink: 0,
  },
  chips: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    flexShrink: 1,
    gap: Spacing.sm,
  },
  statusChip: {
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: Radii.tag,
  },
  statusChipText: {
    fontFamily: Fonts.semiBold,
    fontSize: 11,
    lineHeight: 14,
  },
  facts: {
    flexDirection: 'row',
    gap: Spacing.lg,
    paddingVertical: Spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  fact: {
    flex: 1,
    minWidth: 0,
  },
  factLabel: {
    color: Colors.textLight,
    marginBottom: Spacing.xs,
  },
  factValue: {
    ...Typography.cardTitle,
    color: Colors.textDark,
  },
  descriptionBlock: {
    paddingVertical: Spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  blockLabel: {
    ...Typography.label,
    lineHeight: 20,
    color: Colors.textDark,
    marginBottom: 6,
  },
  description: {
    ...Typography.body,
    lineHeight: 23,
    color: Colors.text,
  },
  sellerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    paddingTop: Spacing.lg,
    paddingBottom: Spacing.xl,
  },
  sellerText: {
    flex: 1,
    minWidth: 0,
  },
  sellerName: {
    color: Colors.text,
  },
  sellerMeta: {
    ...Typography.meta,
    color: Colors.textLight,
  },
  viewCountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: Spacing.base,
  },
  viewCountText: {
    ...Typography.meta,
    color: Colors.textMedium,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.md,
  },
  actionGrow: {
    flex: 1,
    minWidth: 150,
  },
  actionBar: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: Spacing.base,
    paddingTop: Spacing.md,
    borderTopWidth: 1,
    borderTopColor: Colors.borderLight,
    backgroundColor: Colors.white,
  },
});
